import { Fragment } from 'react'

import { ChevronRightIcon } from '@gto/icons-react/outline/chevron-right'

import type { AnnouncementBodyStyle } from './announcement-body-lines'
import { splitAnnouncementBody } from './announcement-body-lines'
import { InlineHighlightedText } from './inline-highlighted-text'

type AnnouncementBodyProps = {
  /** Plain-text body; newlines (and the legacy `<br />` marker) separate lines. */
  body: string
  /** `bulleted` renders a chevron list, `plain` a line-broken paragraph. */
  bodyStyle: AnnouncementBodyStyle
}

/**
 * Renders an announcement body, with its `**bold**` and `==primary==` markers.
 *
 * Both branches emit text nodes and real elements — never
 * `dangerouslySetInnerHTML`. The safety property lives here, in the renderer,
 * not in the field type: whatever `body_style` the backend sends, marketing
 * copy can never become markup.
 */
export function AnnouncementBody({ body, bodyStyle }: AnnouncementBodyProps) {
  const lines = splitAnnouncementBody(body, bodyStyle)

  if (bodyStyle === 'bulleted') {
    return (
      <ul className="flex flex-col gap-1">
        {lines.map((line, index) => (
          <li
            // eslint-disable-next-line @eslint-react/no-array-index-key -- split-once plain-text segments, never reordered; no stable id
            key={index}
            className="flex items-start gap-2"
          >
            <ChevronRightIcon className="mt-1 size-3 shrink-0 text-primary-main" />
            <span>
              <InlineHighlightedText text={line} />
            </span>
          </li>
        ))}
      </ul>
    )
  }

  return (
    <>
      {lines.map((line, index) => (
        // eslint-disable-next-line @eslint-react/no-array-index-key -- split-once plain-text segments, never reordered; no stable id
        <Fragment key={index}>
          {index > 0 && <br />}
          <InlineHighlightedText text={line} />
        </Fragment>
      ))}
    </>
  )
}
